import React, { useContext } from 'react';
import async from 'async';
import {
  User,
  Form,
  Project,
  DataSource,
} from '@fulcrumapp/fulcrum-core';
import { App } from './use-app';

import ExpressionEngine from '../editor/expressions/expression-engine';
import { FormViewParams } from '../models/params/form-view-params';
import queryParams from '../utils/query-params';
import LayerSettings from '../models/layer-settings';
import buildFormColumns from './helpers/form-columns-builder';

export interface FormApp extends App {
  form: Form;

  projects: Project[];

  users: User[];

  params: FormViewParams;

  formUsers: User[];
}
// @ts-ignore
export const FormAppContext = React.createContext<FormApp>(null);

interface PrepareResult {
  form: Form;
  projects: Project[];
  users: User[];
  formUsers: User[];
}

export const fetchRecord = (id, form, dataSource, role?) => new Promise((resolve, reject) => {
  dataSource.getRecord(id, form, (err, record) => {
    if (err) {
      return reject(err);
    }

    record.loadChangeset(dataSource, (err) => {
      if (err) {
        return reject(err);
      }

      if (role) {
        record.updateFromActionAttributes({ ...queryParams() }, role);
      }
      resolve(record);
    });
  });
});

export const createRecord = (form, defaultValues, app) => {
  const record = form.createRecord();

  record.updateFromActionAttributes({ ...queryParams(), ...defaultValues }, app.role);

  return record;
};

export const fetchFormMembers = (formID, users, dataSource, callback) => {
  dataSource.source.getFormMembers(formID, (err, members) => {
    if (err) {
      // @ts-ignore
      return callback(err);
    }

    let formUsers = users;

    if (users.length !== members.length) {
      formUsers = members.map((member) => formUsers.find((user) => user.id === member.user_id)).filter((member) => member);
    }

    callback(null, formUsers);
  });
};

export const prepare = (formID: string, dataSource: DataSource): Promise<PrepareResult> => new Promise((resolve, reject) => {
  dataSource.prepare(formID, (err, objects) => {
    if (err) {
      return reject(err);
    }

    fetchFormMembers(formID, objects.users, dataSource, (err, formUsers) => {
      resolve({
        form: objects.form,
        projects: objects.projects,
        users: objects.users,
        formUsers,
      });
    });
  });
});

export const prepareFromJSON = (formJSON: object, dataSource: DataSource): Promise<PrepareResult> => new Promise((resolve, reject) => {
  const result = {
    form: null,
    users: null,
    projects: null,
  };

  const form = new Form(formJSON);

  const tasks = {
    form: (callback) => {
      // @ts-ignore
      result.form = form;
      // @ts-ignore
      result.form.load(dataSource, callback);
    },

    users: (callback) => {
      dataSource.getUsers(null, (err, users) => {
        if (err) {
          callback(err);
          return;
        }

        result.users = users;
        callback(err);
      });
    },

    projects: (callback) => {
      dataSource.getProjects(null, (err, projects) => {
        if (err) {
          callback(err);
          return;
        }

        result.projects = projects;
        callback(err);
      });
    },
  };

  async.parallel(tasks, (err) => {
    if (err) {
      return reject(err);
    }
    // @ts-ignore
    resolve({ ...result, formUsers: result.users });
  });
});

const initializeObjectCache = (objectCache, versions) => new Promise((resolve) => {
  objectCache.initialize(versions, resolve);
});

export const initializeForm = async (app: App, formID?: string, preparedResults?: PrepareResult): Promise<FormApp> => {
  const params = app.params as FormViewParams;

  ExpressionEngine.initializeRootEngine(app.globals.config.sandbox_url);

  const versions = {
    formVersions: params.form_versions ?? {},
    choiceListVersions: params.choice_list_versions ?? {},
    classificationSetVersions: params.classification_set_versions ?? {},
  };
  // eslint-disable-next-line no-param-reassign
  app.layerSettings = new LayerSettings(
    app.globals.layers ?? [],
    formID || app.params.form_id, // Sonar suggested change it to ?? but || is catching ''
    app.globals.config.tile_url,
    app.globals.config.layer_url ?? app.globals.config.tile_url,
    app.preferences,
  );

  if (app.objectCache) {
    await initializeObjectCache(app.objectCache, versions);
  }

  const result = preparedResults ?? await prepare(formID ?? params.form_id, app.dataSource);

  return {
    ...app, ...result, params,
  };
};

export const useFormApp = (): FormApp => useContext<FormApp>(FormAppContext);

const FORM_QUERY_SCHEMAS: { [key: string]: FormQuerySchema } = {};

export const loadColumns = async (dataSource, formID): Promise<FormQuerySchema> => {
  if (FORM_QUERY_SCHEMAS[formID]) {
    return FORM_QUERY_SCHEMAS[formID];
  }

  const sql = `
SELECT
tables.field AS table_field,
columns.name,
columns.ordinal,
columns.field,
columns.type,
columns.part
FROM columns
INNER JOIN tables ON columns.table_name = tables.name
WHERE
tables.form_id = '${formID}' AND tables.type IN ('repeatable', 'form')
ORDER BY columns.table_name, columns.ordinal
`;

  return new Promise((resolve, reject) => {
    dataSource.source.query({ q: sql }, (err, objects: FormQueryColumn[]) => {
      if (err) {
        reject(err);
        return;
      }

      const formColumns = buildFormColumns(objects);

      FORM_QUERY_SCHEMAS[formID] = formColumns;

      resolve(formColumns);
    });
  });
};

export interface FormQuerySchema {
  // @ts-ignore
  form: FormColumn[];
  repeatables: { [key: string]: FormQueryColumn[] };
}

export interface FormQueryColumn {
  table_field: null;
  name: string;
  ordinal: number;
  field: string | null;
  type: ColumnType;
  part: string | null;
}

export enum ColumnType {
  Array = 'array',
  Double = 'double',
  Geometry = 'geometry',
  Integer = 'integer',
  String = 'string',
  Timestamp = 'timestamp',
}
